import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import {
  hideLoader,
  requestIdleCallback,
  showLoader,
  viewTransition,
} from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

// Module-level shared "page is ready for deferred fetches" signal.
// Becomes true after window 'load' (page fully loaded) OR on the first
// meaningful user interaction (scroll / pointerdown / keydown / touch / wheel).
// Used to defer Shopify subpage fetches in batch mode so the first fold of
// products lands first and the network is not flooded on page load.
let __plReadyForDeferredFetch = false;
/** @type {Set<() => void>} */
const __plReadyCallbacks = new Set();
let __plReadyTrackerInitialized = false;

function __plInitReadyTracker() {
  if (__plReadyTrackerInitialized) return;
  __plReadyTrackerInitialized = true;

  if (typeof window === 'undefined') return;

  const fire = () => {
    if (__plReadyForDeferredFetch) return;
    __plReadyForDeferredFetch = true;
    const pending = [...__plReadyCallbacks];
    __plReadyCallbacks.clear();
    for (const cb of pending) {
      try {
        cb();
      } catch (e) {
        console.error(e);
      }
    }
  };

  // Trigger after the page has finished loading (HTML, images, fonts, etc.).
  if (document.readyState === 'complete') {
    requestIdleCallback(fire);
  } else {
    window.addEventListener(
      'load',
      () => requestIdleCallback(fire),
      { once: true, passive: true }
    );
  }

  // Or trigger on the first meaningful user interaction.
  const interactionEvents = ['pointerdown', 'touchstart', 'keydown', 'scroll', 'wheel'];
  const onInteract = () => {
    for (const evt of interactionEvents) {
      window.removeEventListener(evt, onInteract);
    }
    fire();
  };
  for (const evt of interactionEvents) {
    window.addEventListener(evt, onInteract, { once: true, passive: true });
  }

  // If the user already scrolled before this script ran.
  if (window.scrollY > 0) fire();
}

/**
 * Subscribe to the "ready for deferred fetch" signal. Fires immediately if
 * the signal has already been raised.
 * @param {() => void} callback
 * @returns {() => void} unsubscribe handle
 */
function __plOnReadyForDeferredFetch(callback) {
  __plInitReadyTracker();
  if (__plReadyForDeferredFetch) {
    callback();
    return () => {};
  }
  __plReadyCallbacks.add(callback);
  return () => __plReadyCallbacks.delete(callback);
}

/**
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLSpanElement} [viewMorePrevious]
 * @property {HTMLSpanElement} [viewMoreNext]
 * @property {HTMLElement[]} [cards]
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** @type {Map<number, string>} */
  pages = new Map();

  /** @type {Map<number, Promise<void>>} */
  #inFlightFetches = new Map();

  /** @type {Set<string>} */
  #loadedProductIds = new Set();

  /** @type {Set<string>} */
  #loadedBatches = new Set();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #renderNextInFlight = false;

  /** @type {boolean} */
  #batchSequentialRunning = false;

  /**
   * Serializes the first-batch path only. Once the page is ready for deferred fetches
   * (window.load fires OR the user interacts), {@link #runBatchSequentialFill} continues
   * loading one missing Shopify subpage at a time.
   * @type {Promise<void>}
   */
  #batchLoadQueue = Promise.resolve();

  /** Auto-retries of the batch bootstrap when it failed and left the grid empty (SHPLC-7225). */
  #batchBootstrapRetries = 0;

  /** @type {(() => void) | null} */
  #readyFetchUnsubscribe = null;

  connectedCallback() {
    super.connectedCallback();

    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    const list = this.closest('results-list');
    const infiniteOn = list?.getAttribute('infinite-scroll') === 'true';
    if (!infiniteOn) {
      this.#observeViewMore();
      return;
    }

    const { grid } = this.refs;
    const isBatch =
      grid?.dataset.batchBlockEnd != null && grid.dataset.batchBlockEnd !== '';
    if (isBatch) {
      void this.#initBatchMode();
    } else {
      queueMicrotask(() => this.#seedLoadedProductIds());
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.infinityScrollObserver?.disconnect();
    this.#disconnectReadyFetchSubscription();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Call after the collection section HTML is replaced in-place (e.g. AJAX pagination) so
   * batch runtime state is not reused across logical pages. Same entry point as first load.
   */
  reinitializeBatchAfterSectionUpdate() {
    const { grid } = this.refs;
    if (grid?.dataset.batchBlockEnd == null || grid.dataset.batchBlockEnd === '') return;
    void this.#runBatchModeBootstrap();
  }

  /**
   * Batch: treat the browser URL as the logical page source of truth, ensure the section
   * is rendered from that logical page's first Shopify subpage, then load the rest of the
   * block only after <html> gains .interacted (see theme).
   */
  async #initBatchMode() {
    await this.#runBatchModeBootstrap();
  }

  /**
   * Full batch bootstrap: read the logical page from the URL, reset caches, ensure the section
   * matches that page's first Shopify subpage, then wait for interacted before filling the rest.
   */
  async #runBatchModeBootstrap() {
    this.#detachViewMoreNext();
    this.#syncPlpSkeletonPlaceholders();
    this.#resetBatchState();
    try {
      const pageInfo = this.#getRequestedLogicalPageInfo();
      if (!pageInfo) {
        this.#syncPlpSkeletonPlaceholders();
        return;
      }

      this.#syncBrowserUrlToLogicalPage(pageInfo);
      await this.#ensureRenderedForLogicalPage(pageInfo);
      if (!this.isConnected) return;

      this.#seedLoadedProductIds();
      this.#syncLoadedBatchesFromDom();
      this.#observeViewMore();
      await this.#loadFirstBatchInBlock();
      this.#syncPlpSkeletonPlaceholders();
      this.#registerInteractedForRemainingBatches();
      this.#batchBootstrapRetries = 0;
    } catch (e) {
      console.error(e);
      this.#syncPlpSkeletonPlaceholders();

      // Section fetch failed after its own retries and nothing is on screen: try the whole bootstrap again
      // instead of leaving an empty grid next to a correct item count.
      const grid = this.refs.grid;
      if (
        this.isConnected &&
        grid instanceof HTMLUListElement &&
        this.#getProductCount(grid) === 0 &&
        this.#batchBootstrapRetries < 2
      ) {
        this.#batchBootstrapRetries += 1;
        setTimeout(() => {
          if (this.isConnected) void this.#runBatchModeBootstrap();
        }, 1500 * this.#batchBootstrapRetries);
      }
    } finally {
      this.#reattachViewMoreNext();
    }
  }

  /**
   * Batch PLP: show a grid of skeleton cards only for slots not yet filled by real products
   * (target − current count). Real `li.product-list-grid` items stay visible as they load.
   */
  #syncPlpSkeletonPlaceholders() {
    const results = this.querySelector('#ResultsList');
    const grid = this.refs.grid;

    if (!(grid instanceof HTMLUListElement)) {
      if (results instanceof HTMLElement) {
        results.removeAttribute('aria-busy');
        results.style.removeProperty('visibility');
      }
      return;
    }

    if (!grid.dataset.batchBlockEnd || grid.dataset.batchBlockEnd === '') {
      this.#removePlpSkeletonTrail(grid);
      if (results instanceof HTMLElement) {
        results.removeAttribute('aria-busy');
        results.style.removeProperty('visibility');
      }
      return;
    }

    const target = this.#getTargetCount(grid);
    const count = this.#getProductCount(grid);
    const needed = Math.max(0, target - count);

    const trail = this.#ensurePlpSkeletonTrail(grid);

    if (needed === 0) {
      trail.replaceChildren();
      trail.hidden = true;
      if (results instanceof HTMLElement) {
        results.removeAttribute('aria-busy');
        results.style.removeProperty('visibility');
      }
      return;
    }

    trail.replaceChildren();
    for (let i = 0; i < needed; i++) {
      trail.appendChild(this.#createPlpSkeletonCard());
    }
    trail.hidden = false;

    if (results instanceof HTMLElement) {
      results.style.removeProperty('visibility');
      if (count === 0) {
        results.setAttribute('aria-busy', 'true');
      } else {
        results.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * @param {HTMLUListElement} grid
   */
  #removePlpSkeletonTrail(grid) {
    const next = grid.nextElementSibling;
    if (
      next instanceof HTMLElement &&
      next.dataset.plpSkeletonTrail != null
    ) {
      next.remove();
    }
  }

  /**
   * @param {HTMLUListElement} grid
   * @returns {HTMLDivElement}
   */
  #ensurePlpSkeletonTrail(grid) {
    const next = grid.nextElementSibling;
    if (
      next instanceof HTMLDivElement &&
      next.dataset.plpSkeletonTrail != null
    ) {
      this.#syncTrailLayoutFromGrid(next, grid);
      return next;
    }
    const trail = document.createElement('div');
    trail.dataset.plpSkeletonTrail = '';
    trail.className = 'product-grid-skeleton-trail';
    trail.setAttribute('aria-hidden', 'true');
    trail.hidden = true;
    this.#syncTrailLayoutFromGrid(trail, grid);
    grid.insertAdjacentElement('afterend', trail);
    return trail;
  }

  /**
   * @param {HTMLDivElement} trail
   * @param {HTMLUListElement} grid
   */
  #syncTrailLayoutFromGrid(trail, grid) {
    trail.className = 'product-grid-skeleton-trail';
    trail.dataset.plpSkeletonTrail = '';
    for (const c of grid.classList) {
      if (c.startsWith('product-grid--') || c === 'product-grid-mobile--large') {
        trail.classList.add(c);
      }
    }
    const mc = grid.style.getPropertyValue('--mobile-columns');
    if (mc) {
      trail.style.setProperty('--mobile-columns', mc);
    }
  }

  /** @returns {HTMLDivElement} */
  #createPlpSkeletonCard() {
    const root = document.createElement('div');
    root.className = 'product-grid-skeleton-card';
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML =
      '<div class="product-grid-skeleton-card__media"></div>' +
      '<div class="product-grid-skeleton-card__lines">' +
      '<span class="product-grid-skeleton-card__line product-grid-skeleton-card__line--title"></span>' +
      '<span class="product-grid-skeleton-card__line product-grid-skeleton-card__line--short"></span>' +
      '</div>';
    return root;
  }

  #getBatchConfig(grid = this.refs.grid) {
    if (!grid?.dataset.batchSubpages || !grid.dataset.lastPage) return null;

    const batchSubpages = Number(grid.dataset.batchSubpages);
    const lastShopifyPage = Number(grid.dataset.lastPage);
    const currentShopifyPage = Number(
      grid.dataset.batchShopifyPage || grid.dataset.batchBlockStart || 1
    );

    if (
      !Number.isFinite(batchSubpages) ||
      batchSubpages < 1 ||
      !Number.isFinite(lastShopifyPage) ||
      lastShopifyPage < 1
    ) {
      return null;
    }

    return {
      batchSubpages,
      currentShopifyPage:
        Number.isFinite(currentShopifyPage) && currentShopifyPage > 0 ? currentShopifyPage : 1,
      lastShopifyPage,
      logicalTotalPages: Math.max(1, Math.ceil(lastShopifyPage / batchSubpages)),
    };
  }

  #getLogicalPageForShopifyPage(shopifyPage, batchSubpages) {
    return Math.max(1, Math.floor((shopifyPage - 1) / batchSubpages) + 1);
  }

  #getRequestedLogicalPageInfo(grid = this.refs.grid) {
    const config = this.#getBatchConfig(grid);
    if (!config) return null;

    const currentUrl = new URL(window.location.href);
    const rawPageValue = currentUrl.searchParams.get('page');
    const parsedPage = rawPageValue == null ? Number.NaN : Number(rawPageValue);
    const hasExplicitPage = rawPageValue != null;
    const fallbackPage = this.#getLogicalPageForShopifyPage(
      config.currentShopifyPage,
      config.batchSubpages
    );

    let logicalPage = fallbackPage;
    let shouldNormalize = false;

    if (Number.isFinite(parsedPage) && parsedPage >= 1) {
      const normalizedPage = Math.floor(parsedPage);
      if (normalizedPage <= config.logicalTotalPages) {
        logicalPage = normalizedPage;
      } else if (normalizedPage <= config.lastShopifyPage) {
        logicalPage = this.#getLogicalPageForShopifyPage(
          normalizedPage,
          config.batchSubpages
        );
        shouldNormalize = true;
      } else {
        logicalPage = config.logicalTotalPages;
        shouldNormalize = true;
      }
    } else if (hasExplicitPage) {
      logicalPage = fallbackPage;
      shouldNormalize = true;
    }

    logicalPage = Math.min(config.logicalTotalPages, Math.max(1, logicalPage));

    return {
      ...config,
      hasExplicitPage,
      logicalPage,
      pageParam: rawPageValue,
      shouldNormalize:
        shouldNormalize ||
        (hasExplicitPage && rawPageValue !== String(logicalPage)),
    };
  }

  #syncBrowserUrlToLogicalPage(pageInfo) {
    if (!pageInfo?.shouldNormalize) return;

    const browserUrl = new URL(window.location.href);
    browserUrl.searchParams.set('page', String(pageInfo.logicalPage));

    if (browserUrl.toString() === window.location.href) return;

    try {
      window.history.replaceState({}, '', browserUrl.toString());
    } catch {
    }
  }

  #getShopifyBatchWindowForLogicalPage(pageInfo) {
    if (!pageInfo) return null;

    const start = (pageInfo.logicalPage - 1) * pageInfo.batchSubpages + 1;
    return {
      start,
      end: Math.min(
        pageInfo.lastShopifyPage,
        start + pageInfo.batchSubpages - 1
      ),
    };
  }

  async #ensureRenderedForLogicalPage(pageInfo) {
    const grid = this.refs.grid;
    if (!grid || !pageInfo) return;

    const windowInfo = this.#getShopifyBatchWindowForLogicalPage(pageInfo);
    if (!windowInfo) return;

    const currentStart = Number(grid.dataset.batchBlockStart);
    const currentEnd = Number(grid.dataset.batchBlockEnd);
    const currentShopifyPage = Number(grid.dataset.batchShopifyPage);
    const matchesWindow =
      currentStart === windowInfo.start &&
      currentEnd === windowInfo.end &&
      currentShopifyPage === windowInfo.start;

    if (matchesWindow) return;

    const renderUrl = new URL(window.location.href);
    renderUrl.searchParams.set('page', String(windowInfo.start));
    renderUrl.hash = '';

    await sectionRenderer.renderSection(this.sectionId, {
      cache: false,
      url: renderUrl,
    });
  }

  #resetBatchState() {
    this.#disconnectReadyFetchSubscription();
    this.#batchSequentialRunning = false;
    this.#renderNextInFlight = false;
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;
    this.pages.clear();
    this.#inFlightFetches.clear();
    this.#loadedBatches.clear();
    this.#loadedProductIds.clear();
    this.#batchLoadQueue = Promise.resolve();
  }

  #syncLoadedBatchesFromDom() {
    const g = this.refs.grid;
    if (!g) return;

    const blockStart = Number(g.dataset.batchBlockStart);
    const blockEnd = Number(g.dataset.batchBlockEnd);
    /** @type {Map<string, number>} */
    const pageCounts = new Map();

    g.querySelectorAll('li.product-grid__item[data-page]').forEach((li) => {
      const pg = li.getAttribute('data-page');
      if (!pg) return;

      const pageNumber = Number(pg);
      if (
        Number.isFinite(blockStart) &&
        Number.isFinite(blockEnd) &&
        (pageNumber < blockStart || pageNumber > blockEnd)
      ) {
        return;
      }

      pageCounts.set(pg, (pageCounts.get(pg) || 0) + 1);
    });

    pageCounts.forEach((count, pg) => {
      const pageNumber = Number(pg);
      const expectedCount = this.#getExpectedProductCountForShopifyPage(g, pageNumber);
      if (expectedCount > 0 && count >= expectedCount) {
        this.#loadedBatches.add(pg);
      }
    });
  }

  /**
   * Whether deferred (non first-fold) batch fetches are allowed to start.
   * Becomes true once the page has fully loaded OR the user has interacted.
   */
  #htmlIsInteracted() {
    return __plReadyForDeferredFetch;
  }

  #disconnectReadyFetchSubscription() {
    this.#readyFetchUnsubscribe?.();
    this.#readyFetchUnsubscribe = null;
  }

  /**
   * Load at most the first Shopify subpage in the block (4 products) if it is not already
   * represented in #loadedBatches. Does not load further subpages.
   */
  async #loadFirstBatchInBlock() {
    const grid = this.refs.grid;
    if (!grid?.dataset.batchBlockEnd) return;
    const bs = Number(grid.dataset.batchBlockStart);
    const be = Number(grid.dataset.batchBlockEnd);
    const last = Number(grid.dataset.lastPage);
    if (Number.isNaN(bs) || Number.isNaN(be)) return;
    const beClamped = Math.min(be, last);
    const target = this.#getTargetCount(grid);
    const prunedUnexpectedItems = this.#pruneBatchWindowItemsExcept(grid, bs);
    if (prunedUnexpectedItems) {
      this.#loadedProductIds.clear();
      this.#loadedBatches.clear();
      this.#seedLoadedProductIds();
      this.#syncLoadedBatchesFromDom();
    }

    if (this.#isShopifyPageFullyLoaded(grid, bs)) {
      this.#loadedBatches.add(String(bs));
      return;
    }

    this.#loadedBatches.delete(String(bs));
    const next = this.#nextMissingPageInBlock(grid, bs, beClamped);
    if (next === bs) {
      let attempts = 0;
      while (attempts < 2 && !this.#isShopifyPageFullyLoaded(grid, bs)) {
        attempts += 1;
        this.#loadedBatches.delete(String(bs));
        await this.#batchEnsureSectionHtml(bs, grid);
        await this.#batchApplyMergedPage(bs, grid, target);
        if (!this.#isShopifyPageFullyLoaded(grid, bs) && attempts < 2) {
          this.pages.delete(bs);
        }
      }
    }
    this.#syncPlpSkeletonPlaceholders();
  }

  /**
   * After the first subpage is on screen, the remaining subpages in the block are
   * fetched lazily: only after the page has fully loaded OR the user has interacted
   * (scrolled, tapped, typed, etc.). This keeps the initial page paint focused on
   * the first fold and avoids flooding the network with parallel section fetches.
   */
  #registerInteractedForRemainingBatches() {
    const grid = this.refs.grid;
    if (!grid?.dataset.batchBlockEnd) return;

    this.#disconnectReadyFetchSubscription();
    this.#readyFetchUnsubscribe = __plOnReadyForDeferredFetch(() => {
      this.#readyFetchUnsubscribe = null;
      if (!this.isConnected) return;
      const g = this.refs.grid;
      if (!g?.dataset.batchBlockEnd) return;
      void this.#runBatchSequentialFill();
    });
  }

  #getProductCount(/** @type {HTMLUListElement} */ grid) {
    return grid.querySelectorAll('li.product-grid__item[data-product-id]').length;
  }

  #getTargetCount(/** @type {HTMLUListElement} */ grid) {
    const ppp = Number(grid.dataset.productsPerPage);
    const bsz = Number(grid.dataset.batchSize) || 8;
    const bs = Number(grid.dataset.batchBlockStart);
    const be = Number(grid.dataset.batchBlockEnd);
    const totalResults = Number(grid.dataset.totalResults);

    if (Number.isNaN(ppp) || ppp < 1) return 0;

    let capacity = ppp;
    if (!Number.isNaN(bs) && !Number.isNaN(be) && be >= bs) {
      capacity = Math.min(ppp, (be - bs + 1) * bsz);
    }

    if (!Number.isFinite(totalResults) || totalResults < 0) {
      return capacity;
    }

    if (!Number.isNaN(bs) && bs >= 1) {
      const offsetBeforeBlock = (bs - 1) * bsz;
      const availableOnLogicalPage = Math.max(0, totalResults - offsetBeforeBlock);
      return Math.min(capacity, availableOnLogicalPage);
    }

    return Math.min(capacity, totalResults);
  }

  #getExpectedProductCountForShopifyPage(
    /** @type {HTMLUListElement} */ grid,
    /** @type {number} */ page
  ) {
    const batchSize = Number(grid.dataset.batchSize) || 8;
    const totalResults = Number(grid.dataset.totalResults);

    if (
      !Number.isFinite(page) ||
      page < 1 ||
      !Number.isFinite(batchSize) ||
      batchSize < 1
    ) {
      return 0;
    }

    if (!Number.isFinite(totalResults) || totalResults < 0) {
      return batchSize;
    }

    const offset = (page - 1) * batchSize;
    const remaining = totalResults - offset;
    if (remaining <= 0) return 0;

    return Math.min(batchSize, remaining);
  }

  #getProductCountForShopifyPage(
    /** @type {HTMLUListElement} */ grid,
    /** @type {number} */ page
  ) {
    if (!Number.isFinite(page) || page < 1) return 0;

    return grid.querySelectorAll(
      `li.product-grid__item[data-page="${String(page)}"][data-product-id]`
    ).length;
  }

  #isShopifyPageFullyLoaded(
    /** @type {HTMLUListElement} */ grid,
    /** @type {number} */ page
  ) {
    const expectedCount = this.#getExpectedProductCountForShopifyPage(grid, page);
    if (expectedCount <= 0) return true;

    return this.#getProductCountForShopifyPage(grid, page) >= expectedCount;
  }

  #pruneBatchWindowItemsExcept(
    /** @type {HTMLUListElement} */ grid,
    /** @type {number} */ keepPage
  ) {
    const blockStart = Number(grid.dataset.batchBlockStart);
    const blockEnd = Number(grid.dataset.batchBlockEnd);

    if (Number.isNaN(blockStart) || Number.isNaN(blockEnd)) return false;

    let removedAny = false;
    grid.querySelectorAll(':scope > li.product-grid__item[data-page]').forEach((li) => {
      const pageNumber = Number(li.getAttribute('data-page'));
      if (
        !Number.isFinite(pageNumber) ||
        pageNumber < blockStart ||
        pageNumber > blockEnd ||
        pageNumber === keepPage
      ) {
        return;
      }

      li.remove();
      removedAny = true;
    });

    return removedAny;
  }

  #clearRenderedShopifyPage(
    /** @type {HTMLUListElement} */ grid,
    /** @type {number} */ page
  ) {
    grid
      .querySelectorAll(`:scope > li.product-grid__item[data-page="${String(page)}"]`)
      .forEach((li) => {
        const id = li.getAttribute('data-product-id');
        if (id) {
          this.#loadedProductIds.delete(id);
        }
        li.remove();
      });

    this.#loadedBatches.delete(String(page));
  }

  /**
   * After the page is ready (window.load fired OR user has interacted): fetch and
   * append the remaining Shopify subpages strictly one at a time, in ascending order,
   * so products appear progressively without duplicating and without flooding the
   * network with parallel section-render requests. A short idle yield between
   * subpages keeps the main thread responsive and avoids INP regressions.
   */
  async #runBatchSequentialFill() {
    if (this.#batchSequentialRunning) return;
    const grid = this.refs.grid;
    if (!grid?.dataset.batchBlockEnd) return;

    this.#batchSequentialRunning = true;
    try {
      const bs = Number(grid.dataset.batchBlockStart);
      const be = Number(grid.dataset.batchBlockEnd);
      const last = Number(grid.dataset.lastPage);
      const target = this.#getTargetCount(grid);
      if (Number.isNaN(bs) || Number.isNaN(be)) return;

      const beClamped = Math.min(be, last);
      /** @type {number[]} */
      const missing = [];
      for (let p = bs; p <= beClamped; p++) {
        if (!this.#loadedBatches.has(String(p))) {
          missing.push(p);
        }
      }
      if (missing.length === 0) return;

      const ordered = missing.sort((a, b) => a - b);

      for (let i = 0; i < ordered.length; i++) {
        if (!this.isConnected) return;

        if (this.#getProductCount(grid) >= target) {
          for (let j = i; j < ordered.length; j++) {
            this.#loadedBatches.add(String(ordered[j]));
          }
          break;
        }

        const page = ordered[i];
        try {
          // Fetch this subpage's section HTML on demand (no parallel prefetch).
          await this.#batchEnsureSectionHtml(page, grid);
          await this.#batchApplyMergedPage(page, grid, target);

          // Retry once if the subpage didn't actually populate the grid.
          let attempts = 0;
          while (
            attempts < 1 &&
            this.#getProductCount(grid) < target &&
            !this.#isShopifyPageFullyLoaded(grid, page)
          ) {
            attempts += 1;
            this.#loadedBatches.delete(String(page));
            this.pages.delete(page);
            await this.#batchEnsureSectionHtml(page, grid);
            await this.#batchApplyMergedPage(page, grid, target);
          }
        } catch (e) {
          console.error(e);
        }

        // Yield to the browser between subpages so the main thread can paint
        // newly inserted cards and stay responsive to user input.
        if (i < ordered.length - 1) {
          await new Promise((resolve) => requestIdleCallback(() => resolve(undefined)));
        }
      }
    } finally {
      this.#batchSequentialRunning = false;
      this.#syncPlpSkeletonPlaceholders();
    }
  }

  #nextMissingPageInBlock(/** @type {HTMLUListElement} */ grid, /** @type {number} */ bs, /** @type {number} */ be) {
    for (let p = bs; p <= be; p++) {
      if (!this.#loadedBatches.has(String(p))) return p;
    }
    return null;
  }

  async #loadAndMergeShopifyPage(
    /** @type {number} */ p,
    /** @type {HTMLUListElement} */ grid,
    /** @type {number} */ target
  ) {
    this.#batchLoadQueue = this.#batchLoadQueue
      .then(() => this.#loadAndMergeShopifyPageWork(p, grid, target))
      .catch((e) => {
        console.error(e);
      });
    return this.#batchLoadQueue;
  }

  async #loadAndMergeShopifyPageWork(
    /** @type {number} */ p,
    /** @type {HTMLUListElement} */ grid,
    /** @type {number} */ target
  ) {
    if (this.#loadedBatches.has(String(p))) return;
    if (this.#getProductCount(grid) >= target) {
      this.#loadedBatches.add(String(p));
      return;
    }
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('page', String(p));
    newUrl.hash = '';
    if (!this.#shouldUsePage({ page: p, url: newUrl })) {
      this.#loadedBatches.add(String(p));
      return;
    }
    await this.#batchEnsureSectionHtml(p, grid);
    await this.#batchApplyMergedPage(p, grid, target);
  }

  /**
   * Fetches and caches section HTML for a Shopify subpage. Safe to run many in parallel;
   * deduplicates on `pages` and `#inFlightFetches`.
   * @param {number} p
   * @param {HTMLUListElement} grid
   * @returns {Promise<void>}
   */
  async #batchEnsureSectionHtml(/** @type {number} */ p, /** @type {HTMLUListElement} */ grid) {
    if (this.pages.has(p)) return;
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('page', String(p));
    newUrl.hash = '';
    if (!this.#shouldUsePage({ page: p, url: newUrl })) return;

    const inflight = this.#inFlightFetches.get(p);
    if (inflight) {
      await inflight;
      return;
    }
    const work = (async () => {
      if (!this.#shouldUsePage({ page: p, url: newUrl })) return;
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, newUrl);
      this.pages.set(p, pageContent);
    })();
    this.#inFlightFetches.set(p, work);
    try {
      await work;
    } finally {
      if (this.#inFlightFetches.get(p) === work) {
        this.#inFlightFetches.delete(p);
      }
    }
  }

  /**
   * Inserts product nodes from {@link this.pages} for subpage `p` (same order / dedupe as before).
   * @param {number} p
   * @param {HTMLUListElement} grid
   * @param {number} target
   * @returns {Promise<void>}
   */
  async #batchApplyMergedPage(
    /** @type {number} */ p,
    /** @type {HTMLUListElement} */ grid,
    /** @type {number} */ target
  ) {
    try {
      if (this.#loadedBatches.has(String(p))) return;
      if (this.#getProductCount(grid) >= target) {
        this.#loadedBatches.add(String(p));
        return;
      }
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', String(p));
      newUrl.hash = '';
      if (!this.#shouldUsePage({ page: p, url: newUrl })) {
        this.#loadedBatches.add(String(p));
        return;
      }
      if (!this.pages.has(p)) {
        await this.#batchEnsureSectionHtml(p, grid);
      }
      const items = this.#getGridForPage(p);
      if (!items?.length) {
        if (this.#getExpectedProductCountForShopifyPage(grid, p) <= 0) {
          this.#loadedBatches.add(String(p));
        }
        return;
      }
      this.#clearRenderedShopifyPage(grid, p);
      const list = [...items];
      const toAdd = this.#filterNewProductNodes(list, grid);
      if (toAdd.length) {
        this.#insertItemsForShopifyPageOrder(grid, p, toAdd);
        this.#aspectRatioHelper?.processNewElements();
      }
      if (this.#isShopifyPageFullyLoaded(grid, p)) {
        this.#loadedBatches.add(String(p));
      }
    } finally {
      this.#syncPlpSkeletonPlaceholders();
    }
  }

  #insertItemsForShopifyPageOrder(/** @type {HTMLUListElement} */ grid, /** @type {number} */ p, /** @type {Element[]} */ items) {
    const firstWithHigher = Array.from(
      grid.querySelectorAll('li.product-grid__item')
    ).find((li) => {
      const d = li.dataset?.page;
      if (d == null || d === '') return false;
      return Number(d) > p;
    });
    if (firstWithHigher) {
      for (let k = items.length - 1; k >= 0; k--) {
        firstWithHigher.insertAdjacentElement('beforebegin', items[k]);
      }
    } else {
      grid.append(...items);
    }
  }

  #seedLoadedProductIds() {
    const g = this.refs.grid;
    if (!g) return;
    g.querySelectorAll('li.product-grid__item[data-product-id]').forEach((li) => {
      const id = li.getAttribute('data-product-id');
      if (id) this.#loadedProductIds.add(id);
    });
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            if (this.#batchSequentialRunning) return;
            if (this.#renderNextInFlight) return;
            const { viewMorePrevious, viewMoreNext } = this.refs;
            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else if (entry.target === viewMoreNext) {
              this.#renderNextPage();
            }
          }
        },
        { rootMargin: '100px' }
      );
    }
    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  #detachViewMoreNext() {
    const n = this.refs.viewMoreNext;
    if (n && this.infinityScrollObserver) {
      this.infinityScrollObserver.unobserve(n);
    }
  }

  #reattachViewMoreNext() {
    const n = this.refs.viewMoreNext;
    if (n && this.infinityScrollObserver) {
      requestAnimationFrame(() => {
        if (this.isConnected && n.isConnected) {
          this.infinityScrollObserver?.observe(n);
        }
      });
    }
  }

  #filterNewProductNodes(/** @type {Element[]} */ nodeList, /** @type {HTMLUListElement} */ grid) {
    const out = [];
    for (const node of nodeList) {
      if (!(node instanceof HTMLElement) || !node.classList?.contains('product-grid__item')) {
        continue;
      }
      const id = node.dataset?.productId;
      if (id) {
        if (this.#loadedProductIds.has(id)) continue;
        const safeId = String(id).replace(/"/g, '');
        if (grid.querySelector(`:scope > li.product-grid__item[data-product-id="${safeId}"]`)) {
          continue;
        }
        this.#loadedProductIds.add(id);
        out.push(node);
      }
    }
    return out;
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;
    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    const bs = grid?.dataset.batchBlockStart;
    const be = grid?.dataset.batchBlockEnd;
    if (bs != null && be != null && bs !== '' && be !== '') {
      const a = Number(bs);
      const b = Number(be);
      if (!Number.isNaN(a) && !Number.isNaN(b)) {
        if (pageInfo.page < a || pageInfo.page > b) return false;
      }
    }
    return true;
  }

  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
      return;
    }
    await this.#fetchSpecificPage(page.page, page.url);
    
    if (type === 'next') {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    } else {
      this.#resolvePreviousPagePromise?.();
      this.#resolvePreviousPagePromise = null;
    }
  }

  async #fetchSpecificPage(pageNumber, url = undefined) {
    const newUrl = url || new URL(window.location.href);
    if (!url) {
      newUrl.searchParams.set('page', String(pageNumber));
      newUrl.hash = '';
    }
    const pageInfo = { page: pageNumber, url: newUrl };
    if (!this.#shouldUsePage(pageInfo)) return;
    if (this.pages.has(pageNumber)) return;

    const inflight = this.#inFlightFetches.get(pageNumber);
    if (inflight) {
      await inflight;
      return;
    }

    const work = (async () => {
      /** @type {HTMLElement | null} */
      let loaderEl = null;
      try {
        if (!this.#shouldUsePage({ page: pageNumber, url: newUrl })) return;
        loaderEl = showLoader();
        const pageContent = await sectionRenderer.getSectionHTML(
          this.sectionId,
          true,
          newUrl
        );
        this.pages.set(pageNumber, pageContent);
      } finally {
        if (loaderEl instanceof HTMLElement) {
          hideLoader(loaderEl);
        }
      }
    })();
    this.#inFlightFetches.set(pageNumber, work);
    try {
      await work;
    } finally {
      if (this.#inFlightFetches.get(pageNumber) === work) {
        this.#inFlightFetches.delete(pageNumber);
      }
    }
  }

  async #renderNextPage() {
    if (this.#batchSequentialRunning) return;
    if (this.#renderNextInFlight) return;
    this.#renderNextInFlight = true;
    this.#detachViewMoreNext();
    try {
      const { grid } = this.refs;
      if (!grid) return;
      if (grid.dataset.batchBlockEnd != null && grid.dataset.batchBlockEnd !== '') {
        if (!this.#htmlIsInteracted()) return;
        const target = this.#getTargetCount(grid);
        if (this.#getProductCount(grid) < target) {
          await this.#runBatchSequentialFill();
        }
        if (this.#getProductCount(grid) < target) return;
      }
      const nextPage = this.#getPage('next');
      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      if (this.#loadedBatches.has(String(nextPage.page))) return;

      let nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });
        this.#fetchPage('next');
        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      const list = [...nextPageItemElements];
      const toAppend = this.#filterNewProductNodes(list, grid);
      if (toAppend.length === 0) {
        this.#loadedBatches.add(String(nextPage.page));
        this.pages.delete(nextPage.page);
        return;
      }
      this.#insertItemsForShopifyPageOrder(grid, nextPage.page, toAppend);
      this.#loadedBatches.add(String(nextPage.page));
      this.#aspectRatioHelper?.processNewElements();
      history.pushState('', '', nextPage.url.toString());
      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#renderNextInFlight = false;
      this.#reattachViewMoreNext();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;
    if (grid.dataset.batchBlockEnd != null && grid.dataset.batchBlockEnd !== '') return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });
      this.#fetchPage('previous');
      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }
    const toPrepend = this.#filterNewProductNodes([...previousPageItemElements], grid);
    if (toPrepend.length === 0) {
      this.#loadedBatches.add(String(previousPage.page));
      this.pages.delete(previousPage.page);
      return;
    }
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldH = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;
    grid.prepend(...toPrepend);
    this.#loadedBatches.add(String(previousPage.page));
    this.#aspectRatioHelper?.processNewElements();
    history.pushState('', '', previousPage.url.toString());
    if (firstElement) {
      const newH = firstElement.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: scrollTop + (newH - oldH),
        behavior: 'instant',
      });
    }
    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  #getPage(type) {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return;
    
    const isPrevious = type === 'previous';
    const target = cards[isPrevious ? 0 : cards.length - 1];
    if (!target) return;
    
    const current = Number(target.dataset.page);
    const page = isPrevious ? current - 1 : current + 1;
    
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    
    return { page, url };
  }

  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = () => {
    this.#resetBatchState();
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const { grid } = this.refs;
    const currentLastPage = grid?.dataset.lastPage;
    const currentTotalResults = grid?.dataset.totalResults;

    const reinitAfterFilterMorph = () => {
      if (!this.isConnected) return true;

      const list = this.closest('results-list');
      const activeGrid = this.refs.grid;

      if (list?.getAttribute('infinite-scroll') === 'true' && activeGrid?.dataset.batchBlockEnd) {
        void this.#runBatchModeBootstrap();
        return true;
      }

      if (list?.getAttribute('infinite-scroll') === 'true') {
        this.#seedLoadedProductIds();
        this.#syncLoadedBatchesFromDom();
        this.#observeViewMore();
        this.#fetchPage('next');
        return true;
      }

      this.#syncPlpSkeletonPlaceholders();
      return true;
    };

    const tryReinitIfGridChanged = () => {
      const activeGrid = this.refs.grid;
      if (!(activeGrid instanceof HTMLUListElement)) return false;

      const lastPageChanged = activeGrid.dataset.lastPage !== currentLastPage;
      const totalResultsChanged =
        activeGrid.dataset.totalResults !== currentTotalResults;

      if (!lastPageChanged && !totalResultsChanged) return false;

      this.#resetBatchState();
      reinitAfterFilterMorph();
      return true;
    };

    const observer = new MutationObserver(() => {
      if (tryReinitIfGridChanged()) {
        observer.disconnect();
      }
    });

    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page', 'data-total-results'],
        childList: true,
      });
      setTimeout(() => {
        if (!tryReinitIfGridChanged()) {
          this.#syncPlpSkeletonPlaceholders();
        }
        observer.disconnect();
      }, 3000);
    }
  };
}
